<template>
    <ui-sidepage extra-large :close="asyncClose" :title='`${item.ref} • ${item.description}`' :subtitle="itemSpecsFinalValueFormatted">

        <estimates-old-page-task-item-price-calculator :item="item" :specs-details="specsDetails"></estimates-old-page-task-item-price-calculator>

        <!-- Item options. -->
        <ui-form-section class="my-8">
            <ui-toggle
                label="Create a bundle"
                name="group_use_custom_quantities"
                :disabled="estimate.cannot_be_edited_by_authed_user"
                :value="item.group_use_custom_quantities"
                @input="val => updateCustomQuantities(val)"
                text="Add multiple specs with quantities"
                class="md:col-span-6"
            ></ui-toggle>
            <ui-field label="Bundle mode" v-if="item.group_use_custom_quantities">
                <div class="flex -space-x-px">
                    <label
                        class="flex-1 relative border rounded-l-lg m-0"
                        :class="[
                            item.source_of_truth_cost === 'rate' ? 'bg-blue-50 border-blue-300 z-10' : 'border-gray-200',
                            estimate.cannot_be_edited_by_authed_user ? 'cursor-not-allowed' : 'cursor-pointer',
                        ]"
                    >
                        <div class="p-4 flex space-x-4">
                            <input
                                type="radio"
                                class="h-4 w-4 m-0 mt-1 cursor-pointer text-blue-600 border-gray-300 focus:ring-blue-500"
                                name="source_of_truth_cost"
                                :value="item.source_of_truth_cost === 'rate'"
                                :checked="item.source_of_truth_cost === 'rate'"
                                @input="estimate.cannot_be_edited_by_authed_user ? null : ($event.target.value ? updateSourceOfTruth('rate') : updateSourceOfTruth('amount'))"
                                :disabled="estimate.cannot_be_edited_by_authed_user"
                            >
                            <div>
                                <div class="font-medium text-black">Rate mode</div>
                                <div class="text-sm text-gray-500">Use the total amount of all specs as the <strong class="font-semibold">rate</strong> of the item.</div>
                            </div>
                        </div>
                        <table class="text-sm w-full table-fixed mb-6 shadow-sm">
                            <tr class="text-xs text-gray-500 font-semibold">
                                <td class="w-4 px-2 py-1 border-t border-b border-gray-200 bg-gray-50"></td>
                                <td class="px-2 py-1 border border-gray-200 bg-gray-50">Quantity</td>
                                <td class="px-2 py-1 border border-gray-200 bg-gray-50">Rate</td>
                                <td class="px-2 py-1 border border-gray-200 bg-gray-50">Total</td>
                                <td class="w-4 px-2 py-1 border-t border-b border-gray-200 bg-gray-50"></td>
                            </tr>
                            <tr>
                                <td class="px-2 py-1.5 border-t border-b border-gray-200 bg-white"></td>
                                <td class="px-2 py-1.5 border border-gray-200 bg-white">2 Roofs</td>
                                <td class="px-2 py-1.5 border border-gray-200 bg-white font-semibold">{{ itemSpecsFinalValue | toCurrency }}</td>
                                <td class="px-2 py-1.5 border border-gray-200 bg-white">{{ (itemSpecsFinalValue * 2) | toCurrency }}</td>
                                <td class="px-2 py-1.5 border-t border-b border-gray-200 bg-white"></td>
                            </tr>
                        </table>
                    </label>
                    <label
                        class="flex-1 relative border rounded-r-lg m-0"
                        :class="[
                            item.source_of_truth_cost === 'amount' ? 'bg-blue-50 border-blue-300 z-10' : 'border-gray-200',
                            estimate.cannot_be_edited_by_authed_user ? 'cursor-not-allowed' : 'cursor-pointer',
                        ]"
                    >
                        <div class="p-4 flex space-x-4">
                            <input
                                type="radio"
                                class="h-4 w-4 m-0 mt-1 cursor-pointer text-blue-600 border-gray-300 focus:ring-blue-500"
                                name="source_of_truth_cost"
                                :value="item.source_of_truth_cost === 'amount'"
                                :checked="item.source_of_truth_cost === 'amount'"
                                @input="estimate.cannot_be_edited_by_authed_user ? null : ($event.target.value ? updateSourceOfTruth('amount') : updateSourceOfTruth('rate'))"
                                :disabled="estimate.cannot_be_edited_by_authed_user"
                            >
                            <div>
                                <div class="font-medium text-black">Total mode</div>
                                <div class="text-sm text-gray-500">Use the total amount of all specs as the <strong class="font-semibold">total</strong> of the item.</div>
                            </div>
                        </div>
                        <table class="text-sm w-full table-fixed mb-6 shadow-sm">
                            <tr class="text-xs text-gray-500 font-semibold">
                                <td class="w-4 px-2 py-1 border-t border-b border-gray-200 bg-gray-50"></td>
                                <td class="px-2 py-1 border border-gray-200 bg-gray-50">Quantity</td>
                                <td class="px-2 py-1 border border-gray-200 bg-gray-50">Rate</td>
                                <td class="px-2 py-1 border border-gray-200 bg-gray-50">Total</td>
                                <td class="w-4 px-2 py-1 border-t border-b border-gray-200 bg-gray-50"></td>
                            </tr>
                            <tr>
                                <td class="px-2 py-1.5 border-t border-b border-gray-200 bg-white"></td>
                                <td class="px-2 py-1.5 border border-gray-200 bg-white">10 m2</td>
                                <td class="px-2 py-1.5 border border-gray-200 bg-white">{{ (itemSpecsFinalValue / 10) | toCurrency }}</td>
                                <td class="px-2 py-1.5 border border-gray-200 bg-white font-semibold">{{ itemSpecsFinalValue | toCurrency }}</td>
                                <td class="px-2 py-1.5 border-t border-b border-gray-200 bg-white"></td>
                            </tr>
                        </table>
                    </label>
                </div>
            </ui-field>
        </ui-form-section>

        <!-- Local specs for this item. -->
        <estimates-old-specs-item-sidepage-form
            v-for="itemSpec in itemSpecs"
            :key="itemSpec.spec_id"
            :item="item"
            :item-spec="itemSpec"
            class="my-8"
        ></estimates-old-specs-item-sidepage-form>

        <!-- New Local Spec. -->
        <label class="block text-sm font-medium text-gray-700 mt-8 mb-1" v-if="! estimate.cannot_be_edited_by_authed_user">Specs Previously used in this Estimate</label>
        <div class="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3" v-if="! estimate.cannot_be_edited_by_authed_user">
            <global-specs-button
                v-for="spec in localSpecsToDisplayInGrid"
                :key="spec.id"
                :spec="spec"
                class="hover:bg-gray-100 border border-gray-200 bg-gray-50"
                @click.stop="addLocalSpec(spec)"
            ></global-specs-button>
            <estimates-old-specs-search
                class="col-span-full"
                #default="{ open, query, setQuery }"
                @selected:new="addNewLocalSpec"
                @selected:local="addLocalSpec"
                @selected:global="addGlobalSpec"
                :top="itemSpecs.length > 0"
            >
                <div class="relative">
                    <input
                        placeholder="Type to search or create a new spec..."
                        class="m-0 pl-16 pr-8 py-4 text-md bg-gray-50 border border-gray-200 rounded-lg shadow-none"
                        type="text"
                        :value="query"
                        @input="setQuery($event.target.value)"
                        @focus="open"
                    >
                    <div class="absolute left-0 inset-y-0 flex items-center justify-center w-16 text-gray-400">
                        <i class="fa fa-plus"></i>
                    </div>
                </div>
            </estimates-old-specs-search>
        </div>
    </ui-sidepage>
</template>

<script>
import { GlobalSpec } from '@models'
import { estimateStore } from '@stores'
import { toCurrency } from '@utils'

export default {
    props: ['item', 'close', 'specsDetails'],
    computed: {
        $store () {
            return estimateStore
        },
        estimate () {
            return this.$store.state.estimate
        },
        itemSpecs () {
            return this.item.group
        },
        totalMode () {
            return this.item.group_use_custom_quantities
                && this.item.source_of_truth_cost === 'amount'
        },
        itemSpecsFinalValue () {
            return this.totalMode ? this.item.amount : this.item.rate
        },
        itemSpecsFinalValueFormatted () {
            return this.totalMode
                ? toCurrency(this.itemSpecsFinalValue)
                : (toCurrency(this.itemSpecsFinalValue) + ' per ' + this.item.uom)
        },
        localSpecsToDisplayInGrid () {
            return this.$store.state.localSpecs.slice(0, 9)
        },
    },
    methods: {
        updateCustomQuantities (customQuantities) {
            this.item.group_use_custom_quantities = customQuantities
            this.item.source_of_truth_cost = customQuantities ? 'amount' : 'rate'
            this.save()
        },
        updateSourceOfTruth (sourceOfTruth) {
            this.item.source_of_truth_cost = sourceOfTruth
            this.save()
        },
        save () {
            // Ensure Customer Facing values update from these changes and not markups.
            this.item.computation_strategy = 'cf'
            this.$store.recalculateItem(this.item, true)
            this.$store.save()
        },
        async addNewLocalSpec (query) {
            const localSpec = await GlobalSpec.create({
                estimate_id: this.estimate.id,
                title: query,
                unit_of_measure: this.item.uom,
                nbs: null,
            })
            await this.$store.fetchLocalSpecs()
            this.$store.attachSpecToItem(this.item, localSpec)
        },
        addLocalSpec (spec) {
            this.$store.attachSpecToItem(this.item, spec)
        },
        async addGlobalSpec (spec) {
            const localSpec = await spec.copyAsLocalSpec(this.estimate)
            this.$store.state.localSpecs.push(localSpec)
            this.$store.attachSpecToItem(this.item, localSpec)
        },
        asyncClose () {
            setTimeout(() => {
                this.close()
            }, 0)
        },
    }
}
</script>
